import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { toast } from 'react-toastify';
import { RootState } from 'src/redux/store';
import { PositionsClient, PositionResponse, PositionRequest } from 'src/utility/apis/crewPayApiClient';
import axios from 'src/utility/apis/crewPayAxios';
import { handleError } from 'src/utility/utils';

export interface Position extends PositionResponse { }

export interface State {
  isLoading: boolean;
  error: boolean;
  positionList: Position[];
  positionListForDropdownBox: { value: string, label: string }[];
  selectedPosition?: Position;
}

const initialState: State = {
  isLoading: false,
  error: false,
  positionList: [],
  positionListForDropdownBox: [],
};

const updatePositionListForDropdownBox = (state) => {
  state.positionListForDropdownBox = [...state.positionList]
    .sort((a, b) => a.sortOrder - b.sortOrder)
    .map(p => { return { value: p.positionId.toString(), label: `${p.positionCode} - ${p.positionName}` } });
}

const positionSlice = createSlice({
  name: 'position',
  initialState,
  reducers: {
    startLoading: (state) => {
      state.isLoading = true;
    },
    hasError: (state, action) => {
      state.error = action.payload;
      state.isLoading = false;
    },
    getPositionListSuccess: (state, action: PayloadAction<PositionResponse[]>) => {
      state.positionList = action.payload;
      updatePositionListForDropdownBox(state);
      state.isLoading = false;
    },
    getPositionSuccess: (state, action: PayloadAction<PositionResponse | undefined>) => {
      state.selectedPosition = action.payload;
      state.isLoading = false;
    },
    addNewPositionSuccess: (state, action: PayloadAction<PositionResponse>) => {
      const newPosition = action.payload;
      state.positionList = [newPosition, ...state.positionList];
      updatePositionListForDropdownBox(state);
      state.isLoading = false;
    },
    updatePositionSuccess: (state, action: PayloadAction<PositionResponse>) => {
      const updatedItem = action.payload;
      const index = state.positionList.findIndex(item => item.positionId === updatedItem.positionId);
      state.positionList = [...state.positionList.slice(0, index), updatedItem, ...state.positionList.slice(index + 1)];
      updatePositionListForDropdownBox(state);
      state.isLoading = false;
    },
  },
});

// Reducer
export default positionSlice.reducer

// Actions
export const {
  startLoading,
  hasError,
  getPositionListSuccess,
  getPositionSuccess,
  addNewPositionSuccess,
  updatePositionSuccess,
} = positionSlice.actions;

// for useSelector
export const selectPosition = (state: RootState) => state.position as State;

// ----------------------------------------------------------------------
export function getAllPositions(ignoreCache: boolean = false) {
  return async (dispatch, getState) => {
    try {
      const { positionList } = selectPosition(getState());
      if (ignoreCache || positionList.length === 0) {
        dispatch(positionSlice.actions.startLoading());
        const client = new PositionsClient(undefined, axios);
        const response = await client.getAll(false);
        dispatch(positionSlice.actions.getPositionListSuccess(response));
      }
    } catch (error) {
      dispatch(positionSlice.actions.hasError(true));
      handleError(error);
    }
  };
}

export function getPosition(id: number) {
  return async (dispatch, getState) => {
    if (id > 0) {
      dispatch(positionSlice.actions.startLoading());
      try {
        const client = new PositionsClient(undefined, axios);
        const response = await client.get(id);
        dispatch(positionSlice.actions.getPositionSuccess(response));
      } catch (error) {
        dispatch(positionSlice.actions.hasError(true));
        handleError(error);
      }
    } else {
      dispatch(positionSlice.actions.getPositionSuccess(undefined));
    }
  };
}

export function createPosition(position: Position, showNotification: boolean = true) {
  return async (dispatch, getState) => {
    dispatch(positionSlice.actions.startLoading());
    try {
      const { positionId, ...payload } = position;
      const request = payload as PositionRequest;
      const client = new PositionsClient(undefined, axios);
      const response = await client.create(request);
      dispatch(positionSlice.actions.addNewPositionSuccess(response));
      if (showNotification) {
        toast.success(`${response.positionName} created successfully!`);
      }
    } catch (error) {
      dispatch(positionSlice.actions.hasError(true));
      handleError(error);
    }
  };
}

export function updatePosition(position: Position, showNotification: boolean = true) {
  return async (dispatch, getState) => {
    dispatch(positionSlice.actions.startLoading());
    try {
      const { positionId, ...payload } = position;
      const request = payload as PositionRequest;
      const client = new PositionsClient(undefined, axios);
      const response = await client.edit(positionId, request);
      dispatch(positionSlice.actions.updatePositionSuccess(response));
      if (showNotification) {
        toast.success(`${response.positionName} ${response.isActive ? 'updated' : 'deleted'} successfully!`);
      }
    } catch (error) {
      dispatch(positionSlice.actions.hasError(true));
      handleError(error);
    }
  };
}

export function batchSortOrderUpdate(request: Position[]) {
  return async (dispatch, getState) => {
    try {
      const client = new PositionsClient(undefined, axios);
      const response = await client.batchSortOrderUpdate(request);
      dispatch(positionSlice.actions.getPositionListSuccess(response));
    } catch (error) {
      dispatch(positionSlice.actions.hasError(true));
      handleError(error);
    }
  };
}
